/**
 * UploadStatusBadge Component
 *
 * Renders an upload status value as a coloured badge.
 * The background colour varies depending on the status value to provide
 * at-a-glance visual feedback in table views.
 */

import { Badge } from "@/components/ui/badge"
import { uploadStatusLabelMap } from "@/services/journalArticlesService"

/** Props for the UploadStatusBadge component. */
interface UploadStatusBadgeProps {
  /** The raw upload status value from the article data. */
  status: string
}

/**
 * Map upload status values to Tailwind colour classes.
 * Colours are chosen to convey meaning:
 * - Pending Upload → amber (awaiting action)
 * - Pending Reupload → orange (needs redo)
 * - Uploaded → green (complete)
 * - Reuploaded → green (complete, re-done)
 */
const STATUS_COLOUR_MAP: Record<string, string> = {
  "PENDING_UPLOAD": "bg-amber-100 text-amber-700 border-amber-300",
  "PENDING_REUPLOAD": "bg-orange-100 text-orange-700 border-orange-300",
  "UPLOADED": "bg-green-100 text-green-700 border-green-300",
  "REUPLOADED": "bg-green-100 text-green-700 border-green-300",
}

/** Default colour when the status value is not recognised. */
const DEFAULT_COLOUR = "bg-gray-100 text-gray-700 border-gray-300"

/**
 * Renders an upload status as a coloured badge.
 *
 * @param props - Component props.
 * @param props.status - The raw upload status string.
 * @returns A Badge element with status-dependent colouring.
 */
export default function UploadStatusBadge({ status }: UploadStatusBadgeProps): React.JSX.Element {
  const colourClass = STATUS_COLOUR_MAP[status] ?? DEFAULT_COLOUR
  const label = uploadStatusLabelMap[status] ?? status

  return (
    <Badge variant="outline" className={colourClass}>
      {label}
    </Badge>
  )
}
